'use client';
import { useEffect } from 'react';
import { getConfig } from '@/utils/config/config';
import { AppName } from '@fanduel/braze-content-cards-inbox/types/AppName';
import './styles/default.scss';
import dynamic from 'next/dynamic';
import { useNotificationBtn } from './notification-btn.hook';
import type { PRODUCT_THEME } from '@/utils/constants';
import { useStore } from '@/store/store';
import { ddRumAction } from '@/utils/datadog';
import { useGlobalContext } from '@/store/contexts/global-context';
import { openNotificationsInbox, shouldUseXpNotifications } from '@/hooks/use-notifications-um';
import type { XtremepushConfig } from '@/types/config';
import { CommunicationNotification } from '@fanduel/formation-react-icons';

const WebApp = dynamic(() => import('@fanduel/braze-content-cards-inbox/web/App').then((mod) => mod.default), {
    ssr: false
});

export type NotificationBtnProps = {
    productTheme: PRODUCT_THEME;
};

const XpNotificationButton = ({ config, userId }: { config: XtremepushConfig; userId: string }) => {
    const handleClick = () => {
        void openNotificationsInbox(config, userId);
    };

    return (
        <div data-testid="xp-notification-button">
            <button
                type="button"
                data-testid="notification-button"
                aria-label="Open notifications"
                onClick={handleClick}>
                <CommunicationNotification aria-hidden />
            </button>
        </div>
    );
};

/**
 * TODO
 * After release the useRef fix reinstall @fanduel/braze-content-cards-inbox with stable version
 * Also when we will have precompiled version of @fanduel/braze-content-cards-inbox, uninstall "@types/styled-components"
 * Also they are warning regarding custom styled-components props, because of version upgrade
 * This changes was needed to pass build and test the integration only
 */
export const NotificationBtn = ({ productTheme }: NotificationBtnProps) => {
    const { validateCard, handleClickOfCard } = useNotificationBtn(productTheme);

    const { featureFlags } = useGlobalContext();
    const { braze, xtremepush } = getConfig().services;
    const userId = useStore((state) => state.userContext?.accountSettings?.accountID);
    const useXpNotifications = shouldUseXpNotifications(featureFlags, xtremepush);
    const brazeApiKey = braze?.key;

    useEffect(() => {
        if (!userId) {
            return;
        }

        if (useXpNotifications) {
            ddRumAction({
                name: 'XP_SDK_INIT_USER_ID',
                description: `Xtremepush SDK init with user ID: ${userId}`,
                use_case: 'GLOBAL'
            });
            return;
        }

        if (brazeApiKey) {
            ddRumAction({
                name: 'BRAZE_SDK_INIT_USER_ID',
                description: `BrazeSDK init with user ID: ${userId}`,
                use_case: 'GLOBAL'
            });
        }
    }, [brazeApiKey, useXpNotifications, userId]);

    if (!userId) return null;

    if (useXpNotifications && xtremepush) {
        return <XpNotificationButton config={xtremepush} userId={userId} />;
    }

    if (!brazeApiKey) return null;

    // TODO add suspense fallback when we will have design for it, so we are not shifting the buttons...
    // also we can add error boundary for better handling if webapp will fail to load
    return (
        <WebApp
            key={userId}
            brazeApiKey={brazeApiKey}
            userId={userId}
            appName={AppName.FD_CASINO}
            validateCard={validateCard}
            handleClickOfCard={handleClickOfCard}
        />
    );
};
